<template>
  <div class="audio-player-wrapper">
    <audio
      ref="audioElementRef"
      :src="src"
      preload="metadata"
    />

    <div class="info-wrapper">
      <div class="controls">
        <img
          src="/static/img/rewind_seconds_back.svg"
          alt="rewind_seconds_back"
          @click="backward"
        >

        <div
          class="play-icon"
          @click="togglePlay"
        >
          <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'" />
        </div>

        <img
          src="/static/img/rewind_seconds_forward.svg"
          alt="rewind_seconds_forward"
          @click="forward"
        >

        <img
          src="/static/img/download.svg"
          alt="download"
          @click="download"
        >

        <el-dropdown
          trigger="click"
          class="speed-list"
          @command="updatePlaySpeed"
        >
          <span>
            {{ currentSpeed }}x
            <i class="fa fa-caret-down" />
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="speed in playSpeedList"
                :key="speed"
                :command="speed"
                :disabled="currentSpeed === speed"
              >
                {{ speed }}x
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <span class="time">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </span>
    </div>

    <div class="slider-row">
      <input
        v-model="currentTime"
        class="slider"
        type="range"
        min="0"
        :max="duration"
        step="0.1"
        @input="seek"
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";

const props = defineProps<{
  src: string;
}>();

const audioElementRef = ref<HTMLAudioElement | null>(null);
const currentTime = ref(0);
const duration = ref(0);
const isPlaying = ref(false);
const currentSpeed = ref(1);
const playSpeedList = [0.25, 0.5, 1, 1.5, 2];

onMounted(() => {
  const el = audioElementRef.value;
  if (!el) {
    return;
  }

  el.addEventListener('loadedmetadata', () => {
    duration.value = el.duration || 0;
  });

  el.addEventListener('timeupdate', () => {
    currentTime.value = el.currentTime;
  });

  el.addEventListener('ended', () => {
    isPlaying.value = false;
  });
});

const togglePlay = () => {
  const el = audioElementRef.value;
  if (!el) {
    return;
  }

  if (el.paused) {
    return play();
  }

  pause();
};

const play = () => {
  audioElementRef.value?.play();
  isPlaying.value = true;
};

const pause = () => {
  audioElementRef.value?.pause();
  isPlaying.value = false;
};

const forward = () => {
  if (!audioElementRef.value) {
    return;
  }

  audioElementRef.value.currentTime = Math.min(audioElementRef.value.currentTime + 5, duration.value);
};

const backward = () => {
  if (!audioElementRef.value) {
    return;
  }

  audioElementRef.value.currentTime = Math.max(audioElementRef.value.currentTime - 5, 0);
};

const seek = () => {
  if (!audioElementRef.value) {
    return;
  }

  audioElementRef.value.currentTime = currentTime.value;
};

const download = () => {
  const element = document.createElement('a');
  element.href = props.src;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

const formatTime = (totalSeconds: number) => {
  if (!totalSeconds) {
    return '0:00';
  }

  const mins = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');

  return `${mins}:${seconds}`;
};

const updatePlaySpeed = (speed: number) => {
  if (!audioElementRef.value) {
    return;
  }

  currentSpeed.value = speed;
  audioElementRef.value.playbackRate = speed;
};

defineExpose({ play, pause });
</script>

<style lang="scss">
.audio-player-wrapper {
  padding: 5px 10px;
  border-radius: 10px;
  border: 1px solid #ccc;
  background: #fafafa;

  .info-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .controls {
      display: flex;
      gap: 12px;
    }

    img {
      width: 25px;
      cursor: pointer;
      &:hover {
        opacity: .8;
      }
    }

    .play-icon {
      font-size: 20px;
      width: 20px;
      text-align: center;
      cursor: pointer;
      &:hover {
        opacity: .8;
      }
    }

    .time {
      font-size: 14px;
    }
  }

  .slider-row {
    margin-top: 5px;

    .slider {
      width: 100%;
    }
  }

  .speed-list {
    font-size: 14px;
    color: #252422;
    font-weight: bold;
    line-height: 25px;
    cursor: pointer;
  }
}
</style>